import { Card } from '@/components/ui/card'

import { Button } from '@/components/ui/button'

import { Input } from "@/components/ui/input"

import React from "react"

import { supabase } from '@/lib/supabase'

import { useChat } from 'ai/react'

import { ArrowDownIcon, LoaderCircleIcon, MessageCircle, SendHorizonalIcon, SparkleIcon, SparklesIcon, XIcon } from 'lucide-react'

import { useAuth } from '@/contexts/AuthContext'
import { useOrder } from '@/contexts/OrderContext'
import { OrderLoader } from './Order'
import { cn } from '@/lib/utils'

const { APP_CHAT_URL } = import.meta.env

export default function Bot({ toggleBot }) {
    const { order } = useOrder()

    const [initialMessages, setInitialMessages] = React.useState(null)

    React.useEffect(() => {
        const getInitialMessages = async () => {
            setInitialMessages(null)

            const { data, error } = await supabase
                .from('chats')
                .select('*, profile:profiles(avatar, first_name, last_name)')
                .eq('work_id', order.work.id)
                .order('created_at', { ascending: true })

            setInitialMessages(data)
        }


        if (order?.id) {
            getInitialMessages()
        }
    }, [order?.id])

    return <Card className="flex-grow w-full flex flex-col h-full">
        <div className="border-0 border-b border-solid border-layout flex items-center px-4 py-2 pr-1 text-semibold">
            <SparklesIcon className="mr-2 w-5 h-5 text-primary" /> Assistant

            <Button onClick={toggleBot} size={"icon"} variant={"ghost"} className='ml-auto'>
                <XIcon />
            </Button>
        </div>
        <div className="flex items-center justify-center flex-grow w-full h-full">
            {order?.id && !initialMessages && <OrderLoader icon={MessageCircle} text="Chargement de la conversation" />}
            {(!order?.id || initialMessages) && <Messages initialMessages={initialMessages || []} />}
        </div>
    </Card>
}

function Messages({ initialMessages }) {
    const [autoScroll, setAutoScroll] = React.useState(true)
    const { session } = useAuth()

    const { order } = useOrder() ?? {}

    const { messages, input, handleInputChange, handleSubmit, isLoading, setMessages, error, reload } = useChat({
        api: `${APP_CHAT_URL}/chat`,
        keepLastMessageOnError: true,
        initialMessages: [
            ...initialMessages
        ],
        headers: {
            Authorization: `Bearer ${session.access_token}`,
        },
        body: {
            work_id: order?.work?.id,
        },
    })

    React.useEffect(() => setMessages((messages) => {
        const _messages = messages[0]?.root ? messages.slice(1) : messages
        return [getSystem(Object.values(order?.transcription || {})?.map(t => t.text).join('\n'), order?.mandate?.form), ..._messages]
    }), [order?.mandate?.form, order?.transcription])

    const messagesEndRef = React.useRef(null)
    const messagesRef = React.useRef(null)
    const inputRef = React.useRef(null)

    const scrollBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })

    React.useEffect(() => {
        if (autoScroll) {
            scrollBottom()
        }
    }, [messages, autoScroll])

    React.useEffect(() => {
        if (error) {
            scrollBottom()
        }
    }, [error])

    React.useEffect(() => {
        const handleScroll = () => {
            if (!messagesRef.current) return

            const isAtBottom = messagesRef.current.scrollHeight - messagesRef.current.scrollTop <= messagesRef.current.clientHeight + 16;

            if (isAtBottom) {
                setAutoScroll(true)
            } else {
                setAutoScroll(false)
            }
        }

        messagesRef.current.addEventListener('scroll', handleScroll)
    }, [])

    return <div style={{ width: '100%', height: "100%", position: "relative", display: 'flex', flexDirection: 'column', gap: 8, justifyContent: 'flex-start' }}>
        <div className="flex relative flex-col gap-1.5 px-4 flex-grow">
            <div ref={messagesRef} className="scroll-smooth px-4 py-2 pt-4 space-y-4 box-border absolute top-0 left-0 scroll overflow-auto h-full w-full">
                {/* <Message message={{
                    hide_feedback: true,
                    role: 'system',
                    content: `Bonjour,\nje suis ton assistant de transcription, tu peux me poser des questions sur les termes médicaux, les détails des dossiers, ou tout autre sujet lié à ton travail.`
                }} /> */}
                {messages.filter(message => !message.root).map((message, index) => <Message key={index} message={message} />)}

                {error && <div className="flex flex-col gap-2 items-center mt-2">
                    <span>Une erreur est survenue</span>
                    <Button onClick={reload} variant="outline">Réessayer</Button>
                </div>}
                <div ref={messagesEndRef} />
            </div>
            {!autoScroll && <Button variant="outline" onClick={scrollBottom} size={'icon'} style={{ position: 'absolute', bottom: "16px", left: "50%", transform: "translateX(-50%)" }}><ArrowDownIcon /></Button>}
        </div>

        <div className="border-0 px-4 border-t border-solid border-layout h-14">
            <form className='h-full' onSubmit={!isLoading && handleSubmit}>
                <div className="h-full justify-center flex items-center gap-2 rounded-lg">
                    <Input ref={inputRef} value={input} onChange={handleInputChange} autoFocus placeholder="Poser une question" />
                    <div style={{ transition: 'ease-in 0.1s all' }}>
                        {<Button disabled={isLoading} size="icon">
                            {!isLoading && <SendHorizonalIcon />}
                            {isLoading && <LoaderCircleIcon className="animate-spin" />}
                        </Button>}
                    </div>
                </div>
            </form>
        </div>
    </div>
}

function Message({ message }) {
    return <div
        key={message.id}
        className={cn(
            "whitespace-break-spaces flex w-max max-w-[75%] flex-col gap-2 rounded-lg px-3 py-2 text-sm",
            message.role === "user"
                ? "ml-auto bg-primary text-primary-foreground"
                : "bg-muted"
        )}
    >
        {message.content}
    </div>
}

const getSystem = (transcription, mandate) => {
    return ({
        root: true,
        role: 'system',
        content: `
        Vous êtes un assistant utile et concis pour des transcripteurs médicaux travaillant sur des rapports d'expertise médicale.
        
        Contexte :
        - La transcription est un brouillon issu d'une dictée médicale.
        - Le mandat contient des informations structurées sur le patient, le contexte et l'objectif de l'évaluation.
        
        Votre rôle :
        - Répondre clairement et utilement à toute question du transcripteur.
        - Clarifier les termes médicaux complexes ou ambigus.
        - Suggérer des améliorations grammaticales ou des reformulations de phrases.
        - Vérifier l'alignement du texte avec le mandat.
        
        Contraintes :
        - Ne pas inventer ni ajouter d'information absente de la transcription ou du mandat.
        - Rester concentré sur la transcription et le contexte médical.
        - Si une question dépasse votre champ de compétence, le signaler poliment.
        
        Mandat : ${JSON.stringify(mandate)}
        
        Transcription :
        ${transcription}
        
        En attente d'une question du transcripteur...
        `
    })
}